import dayjs from 'dayjs'
import { isTimeToBeDefined } from '../../../../ts-utils/date-utils'
import { DEFAULT_DURATION_MINUTES, EMPTY_GAME_FORM } from '../constants'
import type { GameEntityLike, GameFormMode, GameFormValues } from '../types'
import {
  hasPlaceholderVisitor,
  readPlaceholderVisitorName,
} from '../../placeholderVisitor/placeholderVisitorKeys'

/** Minutes between two instants, or null when either is missing. */
const durationBetween = (
  start?: string | null,
  end?: string | null
): number | null => {
  if (!start || !end) {
    return null
  }
  const minutes = dayjs(end).diff(dayjs(start), 'minute')
  return Number.isFinite(minutes) ? minutes : null
}

/**
 * Seeds the form.
 *
 * On create everything starts empty except `gameTypeId`, which is pre-selected
 * to Regular Season -- `buildGamePayload` would send that value regardless, so
 * showing it is honest about what gets saved. Duration is deliberately left
 * blank: unlike game type it has no safe default, and pre-filling one risks it
 * being accepted as the real length.
 *
 * On edit the source is the parsed entity from `common/reducers/parsers.js`,
 * whose camelCase field names this mirrors.
 */
export const toGameFormValues = (
  game: GameEntityLike | null | undefined,
  mode: GameFormMode
): GameFormValues => {
  if (mode === 'create' || !game) {
    return { ...EMPTY_GAME_FORM }
  }

  /*
   * Two shapes reach this mapper.
   *
   * The Game PAGE passes the parsed entity from `reducers/parsers.js`, which is
   * camelCase and pre-flattens ids (`homeTeamId`, `facilityId`). The games GRID
   * passes the raw list resource, which is snake_case and keeps nested objects
   * (`homeTeam.id`, `facility.id`). Reading only the first shape opened the
   * modal with every field blank when launched from the grid, so each field
   * falls back to its list-resource equivalent.
   */
  const startsAt = game.startsAt ?? game.starts_at ?? ''
  const endsAt = game.endsAt ?? game.ends_at ?? null

  return {
    // The sentinel start is kept as-is: the date half is real and the duration
    // still computes from it. Only the time-of-day is meaningless, and the
    // toggle below is what tells the UI to render it as TBD.
    startsAt,
    durationMinutes:
      durationBetween(startsAt, endsAt) ?? DEFAULT_DURATION_MINUTES,
    timeToBeDefined: startsAt ? isTimeToBeDefined(startsAt) : false,
    homeTeamId: game.homeTeamId ?? game.homeTeam?.id ?? null,
    visitingTeamId: game.visitingTeamId ?? game.visitingTeam?.id ?? null,
    /*
     * Seeded from the game so reopening the modal shows what was saved. Reads
     * through the shared module, which accepts the parsed camelCase shape and
     * the raw snake_case list resource alike -- the same reason every field
     * above carries a fallback (SN-8410).
     */
    usePlaceholderVisitor: hasPlaceholderVisitor(game),
    visitingTeamPlaceholder: readPlaceholderVisitorName(game) ?? '',
    venueId: game.location?.id ?? game.venue?.id ?? null,
    facilityId: game.facilityId ?? game.facility?.id ?? null,
    locationTimezone: game.location?.timezone ?? game.venue?.timezone ?? null,
    gameNumber: (() => {
      const raw = game.gameNumber ?? game.league_game_number
      return raw === null || raw === undefined ? '' : String(raw)
    })(),
    gameTypeId: game.gameType ?? game.game_type_id ?? null,
    ifNecessaryStatus: Boolean(
      game.ifNecessaryStatus ?? game.if_necessary_status
    ),
    // Legacy fallback preserved: an upcoming entry in the livestream list
    // implies the flag even when the column itself is unset.
    isUpcomingLivestream: Boolean(
      game.is_upcoming_livestream ||
        game.livestreams?.some((l) => l?.video_status === 'upcoming')
    ),
    /*
     * The competition the game is filed under. This is NOT necessarily a
     * division -- a game filed directly on the season carries the season's own
     * id here. The division select only offers the competition's deepest level,
     * so the consumer reconciles this against that list and drops it when it
     * does not appear (see `pickSubCompetitionValue`).
     *
     * Both shapes again, as everywhere above. The Game PAGE passes the parsed
     * entity, which `reducers/parsers.js` has already FLATTENED to a top-level
     * `scheduleId` -- it keeps no nested `schedule` object at all. The games
     * GRID passes the raw list resource, which does. Reading only the nested
     * one seeded null on the Game page, and the division then resolved from
     * nothing to the first option -- silently moving the game.
     */
    scheduleId: game.scheduleId ?? game.schedule?.id ?? null,
  }
}

/**
 * Whether either selected team is a PLACEHOLDER.
 *
 * A placeholder team ("Winner of Semifinal 1", and the like) belongs to no
 * division, so the API cannot infer which one the game lands in. That is the
 * whole reason the division select exists: with a placeholder on either side
 * the admin has to name it, and `schedule_id` becomes required.
 *
 * The flag is read off the TEAM OPTION, not the form values -- `is_placeholder`
 * comes back on `/schedules/:id/teams/simple`, exactly as the legacy
 * `GameTeamPicker` read it. An earlier version compared team ids against
 * tournament bracket SLOT ids, which is a different concept entirely and meant
 * the select never appeared.
 */
export const hasPlaceholderTeam = (
  values: Pick<GameFormValues, 'homeTeamId' | 'visitingTeamId'>,
  teams: Array<{ id: string; is_placeholder?: boolean | null }>,
  /**
   * The game being edited, consulted as a fallback while `teams` is still
   * loading. Legacy read `gameInfo.homeTeam.isPlaceholder` for the same
   * reason: without it the division field stays hidden when the modal opens
   * and pops in once the query resolves.
   */
  game?: GameEntityLike | null
): boolean => {
  const fromOptions = (teamId: string | null) =>
    Boolean(teamId && teams.find((team) => team.id === teamId)?.is_placeholder)

  if (fromOptions(values.homeTeamId) || fromOptions(values.visitingTeamId)) {
    return true
  }

  // Only trusted while the selection still matches what the game was loaded
  // with -- once the admin picks a different team, the options list is the
  // only authority on whether THAT team is a placeholder.
  const fromEntity = (
    teamId: string | null,
    team?: { id?: string | null; isPlaceholder?: boolean | null } | null
  ) => Boolean(teamId && team?.id === teamId && team?.isPlaceholder)

  return (
    fromEntity(values.homeTeamId, game?.homeTeam) ||
    fromEntity(values.visitingTeamId, game?.visitingTeam)
  )
}

/**
 * Whether the game has started or finished.
 *
 * Teams are locked from this point on: stats, rosters and timeline events are
 * already attached to the two sides, and swapping them would leave that history
 * pointing at the wrong team. Ported from the legacy form's
 * `disabled: values.started || values.completed`.
 *
 * Reads BOTH shapes. The Game page supplies the parsed entity, where
 * `reducers/parsers.js` has already turned the timestamps into booleans; the
 * games grid supplies the raw list resource, which only has the timestamps.
 * Checking just the booleans left teams editable on a started game opened from
 * the grid.
 */
export const isGameStarted = (
  game: GameEntityLike | null | undefined
): boolean =>
  Boolean(
    game?.started ||
      game?.completed ||
      typeof game?.started_at === 'string' ||
      typeof game?.ended_at === 'string'
  )
